import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolvePageMeta, isSectionPage } from "../topbar/pageMeta";
import { Button, IconButton, normalizeLegacyType } from "../../ui";
import "./blockTitle.css";

/**
 * Заголовок страницы с кнопками действий.
 *
 * По умолчанию кнопки рендерятся в верхнюю панель приложения
 * (#topbar-actions) в новом стиле — так на странице не остаётся старой «шапки».
 *
 * Внутри карточки пациента (вложенные экраны: УЗИ-протокол, документ медкарты,
 * план лечения и т.п.) кнопки остаются в контенте — отдельной панелью под
 * заголовком, чтобы было видно, к какому объекту они относятся.
 *
 * Единый интерфейс кнопки:
 * buttons: [{
 *   label, onClick,
 *   variant: "primary" | "secondary" | "danger" | "danger-ghost" | "success" | "ghost",
 *   icon: "print" | "trash" | "back" | "edit" | "add" | "save" | ReactElement,
 *   iconOnly?: boolean,
 *   size?: "sm" | "md" | "lg",
 *   // legacy: type: "normal" | "light" | "danger" | "light icon" | "danger icon"
 * }]
 * inline: true | false — принудительно включить/выключить режим «в контенте»
 */
// Вложенная сущность внутри карточки пациента: /patient/:id/<section>/<id>
const isPatientNestedPath = (pathname) => {
    const parts = String(pathname || "")
        .split("/")
        .filter(Boolean);
    return parts[0] === "patient" && parts.length >= 4;
};

const getTitleText = (title) => {
    if (React.isValidElement(title)) {
        return title.props?.title ?? "";
    }
    if (typeof title === "string" || typeof title === "number") {
        return String(title);
    }
    return "";
};

// Текст внутри кнопки (метка может быть JSX: иконка + текст)
const getLabelText = (label) => {
    if (typeof label === "string" || typeof label === "number") {
        return String(label).trim();
    }
    if (React.isValidElement(label)) {
        return React.Children.toArray(label.props?.children)
            .filter(
                (child) =>
                    typeof child === "string" || typeof child === "number",
            )
            .join(" ")
            .trim();
    }
    return "";
};

// Иконки внутри метки (если label — JSX вида <span><svg/>{текст}</span>)
const getLabelIcons = (label) => {
    if (!React.isValidElement(label)) return null;
    const icons = React.Children.toArray(label.props?.children).filter(
        React.isValidElement,
    );
    return icons.length > 0 ? icons : null;
};

// Действия с документом (печать / удаление / возврат) — строго иконки
// без текста: подпись остаётся только в title/aria-label (тултип и
// озвучивание скринридером). «Редактировать» и прочие — с текстом.
const ICON_ONLY_RE =
    /(печат|print|طباعة|удал|delete|حذف|верну|back|رجوع|назад|toList|к списку)/i;

// Семантическая иконка по смыслу подписи: legacy-экраны получают одинаковые
// иконки без правки каждого файла (раньше в каждом был свой inline-SVG).
const ICON_RULES = [
    { icon: "print", re: /(печат|print|طباعة)/i },
    { icon: "trash", re: /(удал|delete|حذف|убрать)/i },
    { icon: "back", re: /(верну|back|رجوع|назад|toList|к списку)/i },
    { icon: "save", re: /(сохран|save)/i },
    { icon: "edit", re: /(редакт|edit|измен|تعديل)/i },
    { icon: "close", re: /(закры|close|إغلاق)/i },
    { icon: "add", re: /(добав|созда|create|add|new|нов|إضافة|إنشاء)/i },
];

const inferIcon = (text) =>
    (ICON_RULES.find(({ re }) => re.test(String(text || ""))) || {}).icon || null;

/**
 * Приводит любое описание кнопки (новое или legacy) к единому виду.
 */
const normalizeButton = (button) => {
    const { variant: legacyVariant, legacyIcon } = normalizeLegacyType(
        button?.type || "normal",
    );
    const labelText = getLabelText(button?.label);
    const labelIcons = getLabelIcons(button?.label);
    const iconOnly =
        typeof button?.iconOnly === "boolean"
            ? button.iconOnly
            : ICON_ONLY_RE.test(labelText) ||
              (legacyIcon && !labelText && Boolean(labelIcons));

    // Иконка: явная → семантическая по тексту подписи («печать», «редактировать»,
    // «сохранить»…) → иконка из JSX-метки (для нестандартных случаев).
    const icon =
        button?.icon ||
        inferIcon(labelText) ||
        (labelText && labelIcons ? labelIcons[0] : null);

    return {
        ...button,
        icon,
        iconOnly: Boolean(iconOnly) && Boolean(icon),
        labelText,
        variant: button?.variant || legacyVariant,
    };
};

/**
 * Одна кнопка действия. В контенте — размер md, в верхней панели — lg.
 * Все кнопки в ряду получают одинаковую высоту.
 */
const renderAction = (button, index, { compact }) => {
    const item = normalizeButton(button);
    const size = item.size || (compact ? "md" : "lg");
    // Если иконка известна — рисуем её из реестра и текст отдельно,
    // иначе оставляем исходную JSX-метку без изменений.
    const content = item.icon && item.labelText ? item.labelText : item.label;

    if (item.iconOnly) {
        return (
            <IconButton
                key={`${item.labelText}-${index}`}
                icon={item.icon}
                onClick={item.onClick}
                size={size}
                title={item.title || item.labelText}
                variant={item.variant}
            />
        );
    }

    return (
        <Button
            key={`${item.labelText}-${index}`}
            icon={item.icon}
            onClick={item.onClick}
            size={size}
            variant={item.variant}>
            {content}
        </Button>
    );
};

const BlockTitle = ({ title, buttons = [], showTitle, inline }) => {
    const { t } = useTranslation("menu");
    const { pathname } = useLocation();
    const [host, setHost] = useState(null);

    useEffect(() => {
        setHost(document.getElementById("topbar-actions"));
    }, []);

    const inContent =
        typeof inline === "boolean" ? inline : isPatientNestedPath(pathname);

    const meta = resolvePageMeta(pathname);
    const pageTitle = meta ? String(t(meta.title) || "").trim() : "";
    const titleText = getTitleText(title).trim();

    const isDuplicate = Boolean(pageTitle) && titleText === pageTitle;
    const shouldShowTitle =
        showTitle === true
            ? Boolean(title)
            : showTitle === false
              ? false
              : Boolean(title) &&
                Boolean(titleText) &&
                !isDuplicate &&
                !isSectionPage(pathname);

    const buttonsList = Array.isArray(buttons) ? buttons : [];
    const actions = buttonsList.map((button, index) =>
        renderAction(button, index, { compact: false }),
    );

    if (!shouldShowTitle && actions.length === 0) {
        return null;
    }

    return (
        <>
            {!inContent && host && actions.length > 0
                ? createPortal(actions, host)
                : null}
            {shouldShowTitle ? (
                <div className="block-title">
                    <div className="block-title-text">{title}</div>
                </div>
            ) : null}
            {inContent && actions.length > 0 ? (
                <div className="block-actions">
                    {buttonsList.map((button, index) =>
                        renderAction(button, index, { compact: true }),
                    )}
                </div>
            ) : null}
        </>
    );
};

export default BlockTitle;
